<script setup lang="ts">
import { requiredRule } from '@/helpers/FormRules';
import { ref, computed, watch, toRef } from 'vue';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import {
  getAllCatalogueGroupsFlat,
  wouldCreateCatalogueCircularReference,
} from '@/composables/catalogue/useCatalogueGroupHierarchy';
import { useCatalogueGroupAutocomplete } from '@/composables/catalogue/useCatalogueGroupAutocomplete';

const { $gettext } = gettext;
const createCatalogueGroupValid = ref(false);

const props = defineProps<{
  modelValue: boolean;
  group?: CatalogueItemGroup;
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', group: Partial<CatalogueItemGroup>): void;
}>();

const form = ref<Partial<CatalogueItemGroup>>({
  name: props.group?.name || '',
  parentUuid: props.group?.parentUuid || undefined,
});

const dialogTitle = computed(() => {
  return props.group ? $gettext('Edit Group') : $gettext('Create Group');
});

// Use the group autocomplete composable
const { autocompleteOptions } = useCatalogueGroupAutocomplete(
  toRef(props, 'groups'),
);

// Filter out groups that would create circular references and convert to autocomplete format
const availableParents = computed(() => {
  // Get all groups as a flat array to avoid duplicates
  const allGroupsFlat = getAllCatalogueGroupsFlat(props.groups);

  // Filter out groups that would create circular references
  const availableGroups = allGroupsFlat.filter((group) => {
    if (!props.group?.uuid) {
      return true;
    } // All groups available for new groups
    return !wouldCreateCatalogueCircularReference(
      group.uuid,
      props.group.uuid,
      allGroupsFlat,
    );
  });

  // Convert to autocomplete format
  return autocompleteOptions.value.filter((option) =>
    availableGroups.some((group) => group.uuid === option.value),
  );
});

const handleSave = () => {
  emit('save', form.value);
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      form.value = {
        name: props.group?.name || '',
        parentUuid: props.group?.parentUuid || undefined,
      };
    }
  },
);

const handleCancel = () => {
  emit('update:modelValue', false);
};
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="500px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="pa-6 bg-white" style="border-radius: 8px">
      <div class="d-flex align-center mb-4">
        <h2 class="text-h5">{{ dialogTitle }}</h2>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleCancel" />
      </div>

      <div>
        <v-form v-model="createCatalogueGroupValid">
          <v-text-field
            v-model="form.name"
            :label="$gettext('Name')"
            class="mb-4"
            :rules="[requiredRule]"
          />

          <v-autocomplete
            v-model="form.parentUuid"
            :items="availableParents"
            item-title="path"
            item-value="value"
            :label="$gettext('Parent Group')"
            clearable
            :hint="
              $gettext('Optional: Select a parent group to create a hierarchy')
            "
            persistent-hint
          >
            <template v-slot:item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <v-list-item-title>{{ item.raw.path }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.raw.path !== item.raw.title">
                  {{ item.raw.title }}
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>

          <!-- Show excluded groups for debugging -->
          <div
            v-if="props.group && availableParents.length < props.groups.length"
            class="mt-2"
          >
            <v-alert type="info" variant="tonal" density="compact">
              {{
                $gettext(
                  'Some groups are excluded to prevent circular references',
                )
              }}
            </v-alert>
          </div>
        </v-form>
      </div>

      <div class="d-flex justify-end mt-6">
        <v-btn
          color="grey-darken-1"
          variant="text"
          class="mr-2"
          @click="handleCancel"
        >
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="text"
          :disabled="!createCatalogueGroupValid"
          @click="handleSave"
        >
          {{ $gettext('Save') }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>
